import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';

import { localize } from 'common/utils';
import MenuEditorSidepanel from 'modules/menu/components/MenuEditorSidepanel';
import { useCreateOptionsTracking } from 'modules/menu/hooks/telemetryHooks';
import { createOption } from 'modules/shared-menu/api';
import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';

import { buildCreateOptionFormData, formValuesToCreateOptionPayload, OptionForm, OptionFormData } from './OptionForm';
import { useSelectionInfoContext } from '../../../contexts/SelectionInfoContext';
import { SuccessToast } from '../../SharedMenuPage/components/CentralisedAddButton';
import { Option } from '../types';

type Props = {
  optionToCopy?: Option;
  onSuccess: (option: Option) => void;
  onClose: () => void;
};

export const NewOptionSidePanel = ({ optionToCopy, onSuccess, onClose }: Props) => {
  const intl = useIntl();
  const { selection } = useSelectionInfoContext();
  const { menuEditorPage } = useListingManagerRoutes();
  const trackCreateOption = useCreateOptionsTracking();
  const [modifiedFields, setModifiedFields] = useState<string[]>([]);

  const showSuccessNotification = useCallback(
    (option: Option) => {
      const optionLink = `${menuEditorPage()}/options?option=${option.id}`;

      toast.success(
        <SuccessToast
          link={optionLink}
          linkLabel={<FormattedMessage id="shared-menu.editor.notifications.create.option.success.link-label" />}
          message={
            <FormattedMessage
              id="shared-menu.editor.notifications.create.option.success.message"
              values={{
                name: <strong>{localize(option.name, selection.primary_language)}</strong>,
              }}
            />
          }
        />,
      );
    },
    [menuEditorPage, selection],
  );

  const {
    mutateAsync: handleSubmit,
    error,
    isLoading,
  } = useMutation(
    (formValues: OptionFormData) =>
      createOption(selection.id, formValuesToCreateOptionPayload(selection.primary_language, formValues)),
    {
      onSuccess: createdOption => {
        onSuccess(createdOption);
        showSuccessNotification(createdOption);
        trackCreateOption(createdOption.id, modifiedFields);
        onClose();
      },
    },
  );

  return (
    <MenuEditorSidepanel
      id={'new-option'}
      title={intl.formatMessage({ id: 'menu-option.add-modal-header' })}
      onClose={onClose}
    >
      <OptionForm
        initialValues={buildCreateOptionFormData(selection.primary_language, optionToCopy)}
        onCancel={onClose}
        onSubmit={(formData, modifiedFields) => {
          setModifiedFields(modifiedFields);
          handleSubmit(formData);
        }}
        error={error}
        submitting={isLoading}
        mode="create"
      />
    </MenuEditorSidepanel>
  );
};
